// AnnouncementsAdminPage.tsx - Admin interface for managing announcements
import React, { useState, useEffect } from 'react';
import { useTimezone } from './lib/TimezoneContext';
import { utcToPickerParts, datetimeLocalToUtc } from './lib/time';
import { PickerTzLabel } from './components/PickerTzLabel';

// ============================================================================
// TYPES
// ============================================================================

interface Announcement {
  id: string;
  scope: 'Global' | 'Guild' | 'Personal';
  title: string;
  subtitle: string | null;
  body: string | null;
  guild_id: string | null;
  guild_name: string | null;
  user_id: string | null;
  is_pinned: boolean;
  priority: number;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

interface Guild {
  id: string;
  name: string;
  tag: string | null;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getStatusBadge(ann: Announcement): { label: string; color: string } {
  if (!ann.is_active) {
    return { label: 'Deleted', color: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30' };
  }
  
  const now = new Date();
  const starts = ann.starts_at ? new Date(ann.starts_at) : null;
  const expires = ann.expires_at ? new Date(ann.expires_at) : null;
  
  if (starts && starts > now) {
    return { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30' };
  }
  if (expires && expires < now) {
    return { label: 'Expired', color: 'bg-gray-500/20 text-[var(--c-ink-2)] border-gray-500/30' };
  }
  return { label: 'Active', color: 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30' };
}

const SCOPE_COLORS: Record<string, string> = {
  Global: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  Guild: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30',
  Personal: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AnnouncementsAdminPage() {
  const userTz = useTimezone();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formScope, setFormScope] = useState<'Global' | 'Guild' | 'Personal'>('Global');
  const [formTitle, setFormTitle] = useState('');
  const [formSubtitle, setFormSubtitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formGuildId, setFormGuildId] = useState('');
  const [formIsPinned, setFormIsPinned] = useState(false);
  const [formPriority, setFormPriority] = useState(0);
  const [formStartsAt, setFormStartsAt] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'expired' | 'deleted'>('active');

  // ---- FETCH DATA ----
  useEffect(() => {
    fetchAnnouncements();
    fetchGuilds();
  }, []);

  async function fetchAnnouncements() {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/announcements', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAnnouncements(data);
      setError(null);
    } catch (e) {
      setError('Failed to load announcements');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchGuilds() {
    try {
      const res = await fetch('/api/admin/guilds-list', { credentials: 'include' });
      if (res.ok) {
        setGuilds(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch guilds:', e);
    }
  }

  // ---- MODAL HANDLERS ----
  function openCreateModal() {
    setEditingId(null);
    setFormScope('Global');
    setFormTitle('');
    setFormSubtitle('');
    setFormBody('');
    setFormGuildId('');
    setFormIsPinned(false);
    setFormPriority(0);
    setFormStartsAt('');
    setFormExpiresAt('');
    setModalOpen(true);
  }

  function openEditModal(ann: Announcement) {
    setEditingId(ann.id);
    setFormScope(ann.scope);
    setFormTitle(ann.title);
    setFormSubtitle(ann.subtitle || '');
    setFormBody(ann.body || '');
    setFormGuildId(ann.guild_id || '');
    setFormIsPinned(ann.is_pinned);
    setFormPriority(ann.priority);
    setFormStartsAt(ann.starts_at ? utcToPickerParts(ann.starts_at, userTz).datetimeLocal : '');
    setFormExpiresAt(ann.expires_at ? utcToPickerParts(ann.expires_at, userTz).datetimeLocal : '');
    setModalOpen(true);
  }

  async function handleSave() {
    if (!formTitle.trim()) {
      alert('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        scope: formScope,
        title: formTitle.trim(),
        subtitle: formSubtitle.trim() || null,
        body: formBody.trim() || null,
        guild_id: formScope === 'Guild' ? formGuildId : null,
        is_pinned: formIsPinned,
        priority: formPriority,
        starts_at: formStartsAt ? datetimeLocalToUtc(formStartsAt, userTz) : null,
        expires_at: formExpiresAt ? datetimeLocalToUtc(formExpiresAt, userTz) : null,
      };

      const url = editingId 
        ? `/api/admin/announcements/${editingId}`
        : '/api/admin/announcements';
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }

      setModalOpen(false);
      fetchAnnouncements();
    } catch (e: any) {
      alert(e.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const res = await fetch(`/api/admin/announcements/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Failed to delete');
      fetchAnnouncements();
    } catch (e) {
      alert('Failed to delete announcement');
    }
  }

  async function handleRestore(id: string) {
    try {
      const res = await fetch(`/api/admin/announcements/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: true }),
      });

      if (!res.ok) throw new Error('Failed to restore');
      fetchAnnouncements();
    } catch (e) {
      alert('Failed to restore announcement');
    }
  }

  // ---- FILTERING ----
  const filteredAnnouncements = announcements.filter(ann => {
    const status = getStatusBadge(ann);
    switch (filterStatus) {
      case 'active': return status.label === 'Active' || status.label === 'Scheduled';
      case 'expired': return status.label === 'Expired';
      case 'deleted': return status.label === 'Deleted';
      default: return true;
    }
  });

  // ---- RENDER ----
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-[var(--c-ink-3)]">Loading announcements...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--c-ink)]">ANNOUNCEMENTS</h1>
          <p className="text-sm text-[var(--c-ink-3)] mt-1">Manage system-wide and guild announcements</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold rounded-lg transition-colors"
        >
          + New Announcement
        </button>
      </div>

      {/* FILTERS */}
      <div className="flex gap-2">
        {(['all', 'active', 'expired', 'deleted'] as const).map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              filterStatus === status
                ? 'bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]'
                : 'bg-transparent border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:border-[var(--c-line-strong)]'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* ANNOUNCEMENTS LIST */}
      <div className="space-y-3">
        {filteredAnnouncements.length === 0 ? (
          <div className="text-center py-12 text-[var(--c-ink-3)]">
            No announcements found
          </div>
        ) : (
          filteredAnnouncements.map(ann => {
            const status = getStatusBadge(ann);
            return (
              <div
                key={ann.id}
                className={`bg-[var(--c-surface)] border rounded-xl p-4 transition-all ${
                  ann.is_active ? 'border-[var(--c-line)] hover:border-[var(--c-line-strong)]' : 'border-red-500/20 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Badges */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${SCOPE_COLORS[ann.scope]}`}>
                        {ann.scope}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${status.color}`}>
                        {status.label}
                      </span>
                      {ann.is_pinned && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded border bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/30">
                          📌 Pinned
                        </span>
                      )}
                      {ann.guild_name && (
                        <span className="text-[10px] text-[var(--c-ink-3)]">
                          → {ann.guild_name}
                        </span>
                      )}
                    </div>

                    {/* Title & Subtitle */}
                    <h3 className="text-[var(--c-ink)] font-semibold">{ann.title}</h3>
                    {ann.subtitle && (
                      <p className="text-sm text-[var(--c-ink-2)] mt-1">{ann.subtitle}</p>
                    )}

                    {/* Meta */}
                    <div className="flex items-center gap-4 mt-3 text-[11px] text-[var(--c-ink-3)]">
                      <span>Created: {formatDate(ann.created_at)}</span>
                      {ann.created_by_name && <span>by {ann.created_by_name}</span>}
                      {ann.expires_at && <span>Expires: {formatDate(ann.expires_at)}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(ann)}
                      className="px-3 py-1.5 text-xs font-medium text-[var(--c-ink-2)] hover:text-[var(--c-ink)] border border-[var(--c-line)] hover:border-[var(--c-line-strong)] rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    {ann.is_active ? (
                      <button
                        onClick={() => handleDelete(ann.id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-500/20 hover:border-red-500/40 rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestore(ann.id)}
                        className="px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 border border-green-500/20 hover:border-green-500/40 rounded-lg transition-colors"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>

                {/* Body Preview */}
                {ann.body && (
                  <div className="mt-3 pt-3 border-t border-[var(--c-line-soft)]">
                    <div className="text-[10px] text-[var(--c-ink-4)] uppercase mb-1">Body Preview</div>
                    <div 
                      className="text-xs text-[var(--c-ink-3)] line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: ann.body }}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-2xl bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-5 border-b border-[var(--c-line)] flex justify-between items-center">
              <h2 className="text-lg font-bold text-[var(--c-ink)]">
                {editingId ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] text-xl">
              ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Scope */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Scope</label>
                <div className="flex gap-2">
                  {(['Global', 'Guild'] as const).map(scope => (
                    <button
                      key={scope}
                      type="button"
                      onClick={() => setFormScope(scope)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        formScope === scope
                          ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-700 dark:text-cyan-300'
                          : 'bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:text-[var(--c-ink)]'
                      }`}
                    >
                      {scope}
                    </button>
                  ))}
                </div>
              </div>

              {/* Guild Selector (if Guild scope) */}
              {formScope === 'Guild' && (
                <div>
                  <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Target Guild</label>
                  <select
                    value={formGuildId}
                    onChange={e => setFormGuildId(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="">Select a guild...</option>
                    {guilds.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name} {g.tag ? `[${g.tag}]` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Title *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="Announcement title"
                  className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm placeholder:text-[var(--c-ink-4)] focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Subtitle (preview text)</label>
                <input
                  type="text"
                  value={formSubtitle}
                  onChange={e => setFormSubtitle(e.target.value)}
                  placeholder="Short description shown in the card"
                  className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm placeholder:text-[var(--c-ink-4)] focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">
                  Body (HTML supported)
                </label>
                <textarea
                  value={formBody}
                  onChange={e => setFormBody(e.target.value)}
                  placeholder="<p>Full announcement content...</p>"
                  rows={6}
                  className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm placeholder:text-[var(--c-ink-4)] focus:border-cyan-500 focus:outline-none font-mono"
                />
                <p className="text-[10px] text-[var(--c-ink-4)] mt-1">
                  Supports HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;a&gt;, etc.
                </p>
              </div>

              {/* Options Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Priority</label>
                  <input
                    type="number"
                    value={formPriority}
                    onChange={e => setFormPriority(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm focus:border-cyan-500 focus:outline-none"
                  />
                  <p className="text-[10px] text-[var(--c-ink-4)] mt-1">Higher = shows first</p>
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formIsPinned}
                      onChange={e => setFormIsPinned(e.target.checked)}
                      className="w-4 h-4 rounded border-[var(--c-line-strong)] bg-[var(--c-surface)] text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-[var(--c-ink-2)]">Pin to top</span>
                  </label>
                </div>
              </div>

              {/* Date Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Starts At</label>
                  <input
                    type="datetime-local"
                    value={formStartsAt}
                    onChange={e => setFormStartsAt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm focus:border-cyan-500 focus:outline-none"
                  />
                  <p className="text-[10px] text-[var(--c-ink-4)] mt-1">Leave empty for immediate · <PickerTzLabel /></p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--c-ink-2)] mb-1.5">Expires At</label>
                  <input
                    type="datetime-local"
                    value={formExpiresAt}
                    onChange={e => setFormExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg text-[var(--c-ink)] text-sm focus:border-cyan-500 focus:outline-none"
                  />
                  <p className="text-[10px] text-[var(--c-ink-4)] mt-1">Leave empty for no expiration · <PickerTzLabel /></p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-[var(--c-line)] flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-[var(--c-ink-2)] hover:text-[var(--c-ink)] border border-[var(--c-line)] hover:border-[var(--c-line-strong)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}